import { useLocation, Link } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { BarChart3, BookOpen, ClipboardList, Database, FileText, FlaskConical, FolderTree, History, Megaphone, MessageSquareWarning, MessagesSquare, Trophy, UserMinus, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import { CONTEST_ONLY_HOME, isContestOnlyAdmin } from "@/config/contest-only-admins";

interface MenuChild {
  title: string;
  url: string;
}
interface MenuItem {
  title: string;
  url: string;
  icon: typeof Users;
  children?: MenuChild[];
}

const menuItems: MenuItem[] = [
  {
    title: "구독 관리",
    url: "/subscriptions",
    icon: Users,
  },
  {
    title: "아티클 관리",
    url: "/articles",
    icon: FileText,
  },
  {
    title: "체인지로그",
    url: "/changelog",
    icon: Megaphone,
  },
  {
    title: "자료집",
    url: "/handbook",
    icon: BookOpen,
    children: [{ title: "분류 체계", url: "/structure" }],
  },
  {
    title: "A/B 테스트",
    url: "/experiments",
    icon: FlaskConical,
  },
  {
    title: "온보딩 설문",
    url: "/survey",
    icon: ClipboardList,
  },
  {
    title: "AI 챗 피드백",
    url: "/chat-feedbacks",
    icon: MessageSquareWarning,
  },
  {
    title: "채팅 분석",
    url: "/analysis",
    icon: BarChart3,
  },
  {
    title: "AI 대화 이력",
    url: "/user-conversations",
    icon: MessagesSquare,
  },
  {
    title: "공모전 응모 이력",
    url: "/contest-submissions",
    icon: Trophy,
  },
  {
    title: "유저 데이터 export",
    url: "/user-data-export",
    icon: Database,
  },
  {
    title: "유저 데이터 뷰어",
    url: "/user-data-viewer",
    icon: FolderTree,
  },
  {
    title: "파일 버전 기록",
    url: "/file-history-viewer",
    icon: History,
  },
  {
    title: "회원 탈퇴 설문",
    url: "/account-deletion-survey",
    icon: UserMinus,
  },
];

export function AppSidebar() {
  const location = useLocation();
  const pathname = location.pathname;

  // 공모전 전용 계정에는 공모전 탭만 노출한다 (UI 노출 제어).
  const email = useAuthStore((s) => s.adminUser?.email);
  const visibleItems = isContestOnlyAdmin(email)
    ? menuItems.filter((item) => item.url === CONTEST_ONLY_HOME)
    : menuItems;

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-12 justify-center border-b border-border/50 px-3">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-foreground">
            <span className="text-xs font-bold text-background">N</span>
          </div>
          <span className="text-sm font-semibold tracking-tight group-data-[collapsible=icon]:hidden">
            Novela Admin
          </span>
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-[11px] uppercase tracking-widest text-muted-foreground">
            콘텐츠 관리
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleItems.map((item) => {
                const selfActive = pathname.startsWith(item.url);
                return (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton
                      asChild
                      isActive={selfActive}
                      className={cn(
                        "h-8 rounded-md text-sm font-normal",
                        selfActive && "bg-accent font-medium"
                      )}
                    >
                      <Link to={item.url}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>

                    {item.children && (
                      <SidebarMenuSub>
                        {item.children.map((child) => {
                          const active = pathname.startsWith(child.url);
                          return (
                            <SidebarMenuSubItem key={child.url}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={active}
                                className={cn(active && "font-medium")}
                              >
                                <Link to={child.url}>
                                  <span>{child.title}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-border/50 p-2">
        <div className="flex items-center gap-2 rounded-md px-2 py-1.5">
          <div className="h-2 w-2 rounded-full bg-emerald-500" />
          <span className="text-[11px] text-muted-foreground group-data-[collapsible=icon]:hidden">
            Production
          </span>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
